import { useEffect, useMemo, useState } from 'react';
import {
  fetchPulseLatest,
  fetchTrends,
  type DwellTier,
  type LaneView,
  type MemberEffort,
  type StateCell,
  type ThroughputHealth,
  type PulseFinding,
  type PulseLatest,
  type TrendsResponse,
} from '../api/work';
import { PipelineDiagram } from '../components/work/pipeline/PipelineDiagram';
import { FindingSidePanel } from '../components/work/insights/FindingSidePanel';
import { LaneHealthCards } from '../components/work/insights/LaneHealthCards';
import { ManpowerHeatmap } from '../components/work/insights/ManpowerHeatmap';
import { NarrativeBlock } from '../components/work/insights/NarrativeBlock';
import { SuggestedChecks } from '../components/work/insights/SuggestedChecks';
import { slugify } from '../components/work/insights/markdown';
import './WorkPage.css';

/**
 * The work view — where the team's Jira and GitHub actually stand.
 *
 * **Every number here is a stored snapshot, not a live query.**
 * `work.trends.snapshot` assembles each window on a timer, so the header
 * states the age in words. The predecessor's panel showed the same
 * figures with no timestamp, and an hour-old number that looks current
 * is worse than one labelled stale.
 *
 * A single page rather than the predecessor's three tabs. Its
 * `PulseInsights`, `Team` and `Workflows` split the same payload across
 * three routes and repeated the lane header in each; the questions people
 * actually ask — is intake outrunning delivery, which lane is jammed, who
 * is over their own baseline — read better in one column.
 */

const DWELL_LABEL: Record<DwellTier, string> = {
  cold: 'moving',
  warm: 'slowing',
  hot: 'stuck',
};

/**
 * Every value `work/trends/health.py` can answer.
 *
 * **`decline` was absent and `stalled` was invented.** The lookup is
 * `HEALTH_LABEL[t.health]`, so a declining lane rendered an *empty* badge
 * — no label, no error, and wingd's TD lane has been declining the whole
 * time. `stalled` is a value the backend has never produced. Both found by
 * typing the diagram against the source rather than against a sample.
 *
 * Exhaustive by construction: `Record<ThroughputHealth, string>` fails to
 * compile if the union gains a member, which is what should have caught
 * this and did not, because the union was wrong too.
 */
const HEALTH_LABEL: Record<ThroughputHealth, string> = {
  healthy: 'healthy',
  watch: 'watch',
  overload: 'overload',
  decline: 'declining',
};

function relativeAge(ms: number): string {
  const sec = Math.floor(ms / 1000);
  if (sec < 90) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

/** A signed integer, with the sign shown even when positive. */
function signed(n: number): string {
  return n > 0 ? `+${n}` : String(n);
}

function pct(share: number): string {
  return `${Math.round(share * 100)}%`;
}

/** Days, at a precision that does not imply more than was measured. */
function days(value: number | null): string {
  if (value === null) return '—';
  return value >= 10 ? `${Math.round(value)}d` : `${value.toFixed(1)}d`;
}

function StateBlock({ state, color }: { state: StateCell; color: string }) {
  const tier = state.dwell_tier;
  return (
    <div className="work-state" title={`${state.transitions_in} entered in window`}>
      <div className="work-state__head">
        <span className="work-state__name">{state.name}</span>
        <span className="work-state__count" style={{ color }}>
          {state.count}
        </span>
      </div>
      <div className="work-state__meta">
        {/* `null` is "not measured", which is not the same as "cold" and
            must not render as a tier. */}
        <span className={`work-tier work-tier--${tier ?? 'none'}`}>
          {tier ? `${days(state.mean_dwell_days)} ${DWELL_LABEL[tier]}` : 'not measured'}
        </span>
        {state.rework_count > 0 && (
          <span
            className="work-state__rework"
            title="Tickets that re-entered this state"
          >
            ↩ {state.rework_count}
          </span>
        )}
      </div>
    </div>
  );
}

function LaneCard({ lane }: { lane: LaneView }) {
  const t = lane.throughput;
  const worstHandoff = useMemo(() => {
    const measured = lane.handoff_latencies.filter(
      (h) => h.median_hours !== null,
    );
    if (measured.length === 0) return null;
    return measured.reduce((a, b) =>
      (b.median_hours ?? 0) > (a.median_hours ?? 0) ? b : a,
    );
  }, [lane.handoff_latencies]);

  return (
    <section className="work-lane" style={{ ['--lane' as string]: lane.color }}>
      <header className="work-lane__head">
        <div className="work-lane__id">
          <span className="work-lane__dot" />
          <h3 className="work-lane__label">{lane.label}</h3>
          <span className="work-lane__projects">
            {lane.projects.join(' · ') || 'no project'}
          </span>
        </div>
        <div className="work-lane__throughput">
          <span className={`work-health work-health--${t.health}`}>
            {HEALTH_LABEL[t.health]}
          </span>
          <span className="work-lane__count">{t.window_total}</span>
          <span className="work-lane__delta">
            {t.pct_change === null
              ? 'no prior window'
              : `${signed(Math.round(t.pct_change * 100))}% vs prior`}
          </span>
        </div>
      </header>

      <div className="work-lane__pipeline">
        {lane.states.map((s) => (
          <StateBlock key={s.name} state={s} color={lane.color} />
        ))}
      </div>

      <footer className="work-lane__foot">
        {worstHandoff && (
          <span title="Slowest hand-off in this lane, by median">
            slowest hand-off{' '}
            <strong>
              {worstHandoff.from_state} → {worstHandoff.to_state}
            </strong>{' '}
            {days((worstHandoff.median_hours ?? 0) / 24)}
            <span className="work-dim"> ({worstHandoff.samples} samples)</span>
          </span>
        )}
        {lane.drifted_members.length > 0 && (
          <span className="work-lane__drifted">
            borrowed:{' '}
            {lane.drifted_members
              .map((m) => `${m.name} (${m.days_sustained}d)`)
              .join(', ')}
          </span>
        )}
      </footer>
    </section>
  );
}

function EffortRow({ member }: { member: MemberEffort }) {
  const wip = member.wip;
  return (
    <div className="work-effort__row">
      <div className="work-effort__who">
        <span className="work-effort__name">{member.person}</span>
        {member.native_lane && (
          <span className="work-dim">{member.native_lane}</span>
        )}
      </div>
      <div
        className="work-effort__bar"
        role="img"
        aria-label={member.split
          .map((s) => `${s.lane} ${pct(s.share)}`)
          .join(', ')}
      >
        {member.split.map((s) => (
          <span
            key={s.lane}
            className={`work-effort__seg work-effort__seg--${s.lane}`}
            style={{ width: pct(s.share) }}
            title={`${s.lane} ${pct(s.share)}`}
          />
        ))}
      </div>
      <div className="work-effort__numbers">
        <span title="Jira actions in window">{member.jira_actions}</span>
        <span title="Commits in window" className="work-dim">
          {member.commits}
        </span>
        {wip ? (
          <span
            className={`work-wip work-wip--${wip.tier}`}
            title={`${wip.current} open against a ${wip.baseline.toFixed(1)} baseline`}
          >
            {wip.current}
            <span className="work-dim">/{wip.baseline.toFixed(0)}</span>
          </span>
        ) : (
          <span className="work-dim">—</span>
        )}
      </div>
    </div>
  );
}

export function WorkPage() {
  const [resp, setResp] = useState<TrendsResponse | null>(null);
  const [window, setWindow] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pulse, setPulse] = useState<PulseLatest | null>(null);
  const [selected, setSelected] = useState<PulseFinding | null>(null);
  const [highlighted, setHighlighted] = useState<string | null>(null);

  /**
   * The narrative and its findings, fetched separately from the view.
   *
   * Separate because they fail separately and for different reasons: the
   * view is assembled by `work.trends.snapshot` and the narrative composed
   * by `work.pulse`, so either can be absent while the other is current.
   * Folding them into one `Promise.all` would make a missing narrative
   * blank the diagram, which is the larger half of the page.
   */
  useEffect(() => {
    let live = true;
    fetchPulseLatest(window)
      .then((p) => live && setPulse(p))
      .catch(() => live && setPulse(null));
    return () => {
      live = false;
    };
  }, [window]);

  /**
   * Clicking a finding selects it, and flashes the matching narrative
   * heading.
   *
   * The cross-link is a slug of the headline, computed identically on both
   * sides — the heading's `id` comes from `slugify` in `markdown.tsx` and so
   * does this. The highlight clears itself: it says "here", and a highlight
   * that persisted would read as a selection the reader has to undo.
   */
  useEffect(() => {
    if (highlighted === null) return;
    // Bare `setTimeout`, not `window.setTimeout`: the state variable
    // `window` (the trends window, a number) shadows the global throughout
    // this component, so the qualified form would be a property access on
    // a number. It type-checks as `any` in looser configs and fails at
    // runtime.
    const id = setTimeout(() => setHighlighted(null), 2000);
    return () => clearTimeout(id);
  }, [highlighted]);

  const openFinding = (f: PulseFinding) => {
    setSelected(f);
    const slug = slugify(f.headline);
    setHighlighted(slug);
    // After paint, so the heading exists to scroll to.
    requestAnimationFrame(() => {
      document
        .getElementById(`finding-${slug}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  useEffect(() => {
    let live = true;
    setLoading(true);
    fetchTrends(window)
      .then((r) => {
        if (!live) return;
        setResp(r);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!live) return;
        // The route answers 404 before the first snapshot and 503 when
        // `work.trends` is not enabled. Both are states an operator can
        // act on, so neither is flattened into "failed to load".
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [window]);

  const view = resp?.view;
  const unmatched = useMemo(() => {
    const u = view?.effort.unmatched ?? {};
    return Object.entries(u).filter(([, names]) => names.length > 0);
  }, [view]);

  if (error) {
    return (
      <div className="work-page">
        <div className="work-empty">
          <h1 className="work-page__title">Work</h1>
          <p>{error}</p>
          <p className="work-dim">
            The view is assembled by <code>work.trends.snapshot</code> on a
            timer. Before its first run there is nothing stored to show.
          </p>
        </div>
      </div>
    );
  }

  if (!view || !resp) {
    return (
      <div className="work-page">
        <div className="work-empty">
          <h1 className="work-page__title">Work</h1>
          <p className="work-dim">{loading ? 'Loading…' : 'No view stored.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="work-page">
      <header className="work-page__header">
        <div>
          <h1 className="work-page__title">Work</h1>
          <p className="work-page__headline">{view.headline}</p>
        </div>
        <div className="work-page__controls">
          <div className="work-windows" role="group" aria-label="Window">
            {resp.windows.map((w) => (
              <button
                key={w}
                type="button"
                className={`work-window${w === resp.window_days ? ' is-active' : ''}`}
                onClick={() => setWindow(w)}
              >
                {w}d
              </button>
            ))}
          </div>
          <span className="work-age" title={new Date(resp.computed_at_ms).toISOString()}>
            assembled {relativeAge(resp.age_ms)}
          </span>
        </div>
      </header>

      {/* The predecessor's element order, which is an argument: the checks
          you could act on, then the picture, then the per-lane detail, then
          the prose that explains all three. Acting comes before reading. */}
      <SuggestedChecks
        findings={pulse?.findings ?? []}
        onSelect={openFinding}
      />

      {/* The diagram, because it is what the numbers below are a summary of.

          The flow bar stays underneath rather than being replaced by it: it
          carries `delta_vs_previous` for both ends, which the diagram's
          input and outcome panels do not show. */}
      <PipelineDiagram view={view} />

      {/* `view.patterns` — computed by this backend since the Work page
          existed and rendered nowhere until now. */}
      <LaneHealthCards view={view} />

      {/* Where the predecessor had it: after the lane health, before the
          narrative. Its own fetch — open work is "now", so it does not
          follow this page's window. */}
      <ManpowerHeatmap lanes={view.lanes} />

      <section className="work-flow">
        <div className="work-flow__side">
          <span className="work-flow__label">arriving</span>
          <span className="work-flow__total">{view.input.total}</span>
          <span className="work-flow__delta">
            {signed(view.input.delta_vs_previous)} vs prior
          </span>
        </div>
        <div className="work-flow__mix" aria-hidden="true">
          {view.lanes.map((l) => {
            const share = view.intent_mix[l.category] ?? 0;
            return (
              <span
                key={l.category}
                className="work-flow__seg"
                style={{ width: pct(share), background: l.color }}
                title={`${l.label} ${pct(share)} of intake`}
              />
            );
          })}
        </div>
        <div className="work-flow__side work-flow__side--right">
          <span className="work-flow__label">finished</span>
          <span className="work-flow__total">{view.outcome.total}</span>
          <span className="work-flow__delta">
            {signed(view.outcome.delta_vs_previous)} vs prior
          </span>
        </div>
      </section>

      <div className="work-lanes">
        {view.lanes.map((l) => (
          <LaneCard key={l.category} lane={l} />
        ))}
      </div>

      <section className="work-effort">
        <header className="work-effort__head">
          <h2 className="work-section__title">Where effort went</h2>
          <span className="work-dim">
            jira · commits · open/baseline
          </span>
        </header>
        {view.effort.members.map((m) => (
          <EffortRow key={m.person} member={m} />
        ))}
        {unmatched.length > 0 && (
          <p className="work-unmatched">
            {unmatched.map(([source, names]) => (
              <span key={source}>
                <strong>{names.length}</strong> unmatched {source} actor
                {names.length === 1 ? '' : 's'}
                <span className="work-dim"> ({names.slice(0, 4).join(', ')}
                  {names.length > 4 ? ', …' : ''})</span>
              </span>
            ))}
            <span className="work-dim">
              — effort the split above does not account for.
            </span>
          </p>
        )}
      </section>

      {view.drift.length > 0 && (
        <section className="work-drift">
          <h2 className="work-section__title">Sustained drift</h2>
          <ul className="work-drift__list">
            {view.drift.map((d) => (
              <li key={`${d.person}-${d.to_lane}`}>
                <strong>{d.person}</strong>
                <span className="work-dim"> {d.from_lane} → </span>
                <strong>{d.to_lane}</strong>
                <span className="work-dim"> for {d.days_sustained}d</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {/* Last, because it is prose about everything above it. The
          cross-link runs both ways: a chip scrolls to a heading here, and
          the heading flashes to say which one. */}
      <NarrativeBlock run={pulse?.run ?? null} highlightedSlug={highlighted} />

      <FindingSidePanel finding={selected} onClose={() => setSelected(null)} />

    </div>
  );
}
